// Modal.jsx
import { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { IconButton } from './Button';
import Button from './Button';

const SIZE_MAP = {
  xs: 320,
  sm: 420,
  md: 560,
  lg: 720,
  xl: 880,
  '2xl': 1024,
  '3xl': 1280,
  full: '100%'
};

const Modal = ({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
  showCloseButton = true,
  closeOnOverlayClick = true,
  closeOnEscape = true,
  className = '',
  overlayClassName = '',
  contentClassName = ''
}) => {
  const modalRef = useRef(null);
  const previousActiveElement = useRef(null);

  useEffect(() => {
    const handleEscape = (e) => {
      if (!isOpen) return;
      if (closeOnEscape && e.key === 'Escape') onClose?.();
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      previousActiveElement.current = document.activeElement;
      // Focus the modal container for keyboard users
      setTimeout(() => modalRef.current?.focus(), 0);
      // Prevent body scroll
      const prevOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.removeEventListener('keydown', handleEscape);
        document.body.style.overflow = prevOverflow || '';
        // restore focus
        previousActiveElement.current?.focus?.();
      };
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, closeOnEscape, onClose]);

  const handleOverlayClick = (e) => {
    if (closeOnOverlayClick && e.target === e.currentTarget) {
      onClose?.();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key !== 'Tab') return;
    const focusable = modalRef.current?.querySelectorAll(
      'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
    );
    if (!focusable || focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (e.shiftKey && document.activeElement === first) {
      last.focus();
      e.preventDefault();
    } else if (!e.shiftKey && document.activeElement === last) {
      first.focus();
      e.preventDefault();
    }
  };

  const maxWidth = SIZE_MAP[size] ?? SIZE_MAP.md;
  const contentStyle = {
    width: '100%',
    maxWidth: typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth,
    maxHeight: '90vh',
    background: 'white',
    borderRadius: 12,
    boxShadow: '0 30px 60px rgba(2,6,23,0.16)',
    overflow: 'hidden',
    border: '1px solid rgba(14, 20, 25, 0.04)'
  };

  const overlayStyle = {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.45)',
    backdropFilter: 'blur(4px)'
  };

  const containerStyle = {
    position: 'fixed',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '1rem',
    zIndex: 60
  };

  const headerStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '1rem 1.25rem',
    borderBottom: '1px solid rgba(14,20,25,0.04)',
    gap: 12
  };

  const titleStyle = {
    margin: 0,
    fontSize: '1.125rem',
    fontWeight: 600,
    color: 'var(--gray-900, #0f172a)'
  };

  const bodyStyle = {
    padding: '1rem 1.25rem',
    overflowY: 'auto',
    maxHeight: 'calc(90vh - 160px)'
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div
          className={className}
          aria-hidden={!isOpen}
          role="presentation"
        >
          {/* Overlay */}
          <motion.div
            className={overlayClassName}
            style={overlayStyle}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            onClick={handleOverlayClick}
          />

          {/* Container */}
          <div style={containerStyle}>
            <motion.div
              ref={modalRef}
              role="dialog"
              aria-modal="true"
              aria-labelledby={title ? 'modal-title' : undefined}
              tabIndex={-1}
              onKeyDown={handleKeyDown}
              className={contentClassName}
              style={contentStyle}
              initial={{ opacity: 0, scale: 0.98, y: 8 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.98, y: 8 }}
              transition={{ duration: 0.18, ease: 'easeOut' }}
            >
              {(title || showCloseButton) && (
                <div style={headerStyle}>
                  {title ? (
                    <h2 id="modal-title" style={titleStyle}>
                      {title}
                    </h2>
                  ) : (
                    <div />
                  )}

                  {showCloseButton && (
                    <IconButton
                      variant="ghost"
                      size="sm"
                      onClick={() => onClose?.()}
                      aria-label="Close modal"
                      style={{ marginLeft: 8 }}
                    >
                      <X style={{ width: 16, height: 16 }} />
                    </IconButton>
                  )}
                </div>
              )}

              <div style={bodyStyle}>{children}</div>
            </motion.div>
          </div>
        </div>
      )}
    </AnimatePresence>
  );
};

// Subcomponents
export const ModalHeader = ({ children, className = '', style = {} }) => (
  <div
    className={className}
    style={{
      padding: '1rem 1.25rem',
      borderBottom: '1px solid rgba(14,20,25,0.04)',
      ...style
    }}
  >
    {children}
  </div>
);

export const ModalBody = ({ children, className = '', style = {} }) => (
  <div className={className} style={{ padding: '1rem 1.25rem', ...style }}>
    {children}
  </div>
);

export const ModalFooter = ({ children, className = '', style = {} }) => (
  <div
    className={className}
    style={{
      padding: '0.75rem 1.25rem',
      borderTop: '1px solid rgba(14,20,25,0.04)',
      background: 'var(--gray-50, #fbfdff)',
      display: 'flex',
      justifyContent: 'flex-end',
      gap: 8,
      ...style
    }}
  >
    {children}
  </div>
);

// Confirm modal convenience component
export const ConfirmModal = ({
  isOpen,
  onClose,
  onConfirm,
  title = 'Confirm Action',
  message = 'Are you sure you want to proceed?',
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  confirmVariant = 'primary',
  loading = false
}) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="sm" showCloseButton={false}>
      <ModalBody>
        <div style={{ textAlign: 'center', padding: '0.5rem 0 1rem 0' }}>
          <h3 style={{ margin: 0, fontSize: '1.125rem', fontWeight: 600, color: 'var(--gray-900)' }}>
            {title}
          </h3>
          <p style={{ marginTop: 8, color: 'var(--gray-600)' }}>{message}</p>

          <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
            <Button variant="secondary" onClick={onClose} disabled={loading}>
              {cancelText}
            </Button>

            <Button variant={confirmVariant} onClick={onConfirm} loading={loading}>
              {confirmText}
            </Button>
          </div>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default Modal;
